import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebouncedValue } from '@/lib/hooks';
import Masonry from 'react-masonry-css';
import {
    IconSearch, IconSparkles, IconAdjustmentsHorizontal,
    IconLayoutGrid, IconRectangleVertical, IconRectangle, IconSquare,
    IconTrendingUp, IconFlame, IconClock, IconSortAscendingLetters, IconArrowUp, IconRefresh,
    IconArrowsSort, IconChevronDown, IconCalendar, IconLoader2, IconX,
    IconUsers, IconGenderMale, IconGenderFemale, IconMoodKid, IconShoppingBag,
    IconSortAscending, IconSortDescending, IconHeart, IconHeartFilled,
} from '@tabler/icons-react';
import aiTemplateService from '../../../services/aiTemplateService';
import aiTemplateCategoryService from '../../../services/aiTemplateCategoryService';
import { imageColorBucket } from '../../../utils/imageColorBucket';
import { Sheet, SheetContent, SheetTitle } from '../../../components/ui/sheet';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuLabel, DropdownMenuSeparator,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '../../../components/ui/dropdown-menu';
import { useAuthStore } from '../../../store/authStore';
import { useAiTemplateCacheStore } from '../../../store/aiTemplateCacheStore';
import { useAiTemplateFavoritesStore } from '../../../store/aiTemplateFavoritesStore';
import LowCreditBanner from '../../../components/common/LowCreditBanner';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

// imageSize → orientation bucket used by the sidebar Orientation filter.
const ORIENTATION_OF = {
    portrait_2x3: 'portrait',
    portrait_4x5: 'portrait',
    square_1x1: 'square',
    landscape_3x2: 'landscape',
    landscape_16x9: 'landscape',
};

const PAGE_SIZE = 18;

// Date-range filter → epoch-ms cutoff. Templates with createdAt >= cutoff pass.
// 'today' = since local midnight; the rest are rolling windows from now.
function dateRangeCutoff(range) {
    const now = new Date();
    if (range === 'today') {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }
    const days = { '3days': 3, '7days': 7, '30days': 30 }[range];
    if (!days) return null;
    return now.getTime() - days * 24 * 60 * 60 * 1000;
}

const masonryBreakpoints = { default: 5, 1920: 5, 1440: 4, 1280: 3, 1024: 2, 640: 1 };

// react-masonry-css distributes cards by index (item i → column i % cols), which
// leaves the shorter columns with large trailing gaps. This balanced grid instead
// greedily places each card into the currently-shortest column, estimating card
// height from the thumbnail's aspect ratio so columns stay even.
const IMG_ASPECT_H = {
    portrait_2x3: 1.5,
    portrait_4x5: 1.25,
    square_1x1: 1.0,
    landscape_3x2: 0.667,
    landscape_16x9: 0.5625,
};
// CSS aspect-ratio (width / height) per imageSize, used to reserve the thumbnail's
// height BEFORE the image loads so the card never collapses to 0-height then reflows.
const IMG_ASPECT_RATIO = {
    portrait_2x3: '2 / 3',
    portrait_4x5: '4 / 5',
    square_1x1: '1 / 1',
    landscape_3x2: '3 / 2',
    landscape_16x9: '16 / 9',
};
const CARD_FOOTER_H = 0.32; // footer + margin, relative to column width

function BalancedMasonry({ items, columnCount, renderItem, heightOf }) {
    const columns = useMemo(() => {
        const cols = Array.from({ length: columnCount }, () => []);
        const heights = new Array(columnCount).fill(0);
        for (const it of items) {
            const est = heightOf(it);
            let min = 0;
            for (let i = 1; i < columnCount; i++) if (heights[i] < heights[min]) min = i;
            cols[min].push(it);
            heights[min] += est;
        }
        return cols;
    }, [items, columnCount, heightOf]);

    return (
        <div className="flex w-auto -ml-4">
            {columns.map((col, i) => (
                <div key={i} className="pl-4 bg-clip-padding flex-1 min-w-0">
                    {col.map(renderItem)}
                </div>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Gallery — flat masonry grid + sticky filter sidebar
// ---------------------------------------------------------------------------
export default function AiTemplateGallery() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, i18n } = useTranslation();
    const { user } = useAuthStore();
    const effectiveCreditForBanner = user?.credit ?? 0;

    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Client-side 12h TTL cache (persisted to localStorage). After a single
    // visit the page serves from cache with zero network calls until expiry.
    const cacheGetTemplates = useAiTemplateCacheStore((s) => s.getFreshTemplates);
    const cacheGetCategories = useAiTemplateCacheStore((s) => s.getFreshCategories);
    const cacheSetTemplates = useAiTemplateCacheStore((s) => s.setTemplates);
    const cacheSetCategories = useAiTemplateCacheStore((s) => s.setCategories);

    // Per-user favorites (❤️ on cards) — separate store so hearts stay fresh
    // even while the template list itself serves from the 12h cache.
    const favSet = useAiTemplateFavoritesStore((s) => s.favorites);
    const toggleFavorite = useAiTemplateFavoritesStore((s) => s.toggle);
    const loadFavorites = useAiTemplateFavoritesStore((s) => s.load);
    useEffect(() => { loadFavorites(); }, [loadFavorites]);

    // ── Filter state ──────────────────────────────────────────────────────
    // Column count derives from the ACTUAL grid width (viewport minus the sidebar),
    // targeting ~250px per card, so wider screens automatically pack more per row.
    const gridRef = useRef(null);
    const [gridWidth, setGridWidth] = useState(0);
    useEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const measure = () => setGridWidth(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const columnCount = gridWidth ? Math.max(1, Math.min(6, Math.floor(gridWidth / 250))) : 3;
    // Measured thumbnail aspect ratios (height/width) keyed by template id — filled
    // on image load so the masonry balances from real heights, not just the coarse
    // imageSize bucket. Falls back to the bucket (then a portrait default) until loaded.
    const [ratios, setRatios] = useState({});
    const reportRatio = useCallback((id, r) => {
        if (!r || !isFinite(r)) return;
        setRatios((prev) => (prev[id] ? prev : { ...prev, [id]: r }));
    }, []);
    const heightOf = useCallback(
        (it) => (ratios[it.id] ?? IMG_ASPECT_H[it.imageSize] ?? 1.333) + CARD_FOOTER_H,
        [ratios]
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [allTags, setAllTags] = useState([]); // for the tag marquee
    const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
    const [orientation, setOrientation] = useState('all');
    const [imageCount, setImageCount] = useState('all');
    const [color, setColor] = useState('all');
    const [gender, setGender] = useState('all');
    // Default view = Trending (most-used in the last 7 days); Newest is one click away.
    const [sort, setSort] = useState('trending');
    // Direction toggle applied to the active Newest/Popular tab: 'desc' = newest /
    // most-popular first (default), 'asc' = oldest / least-popular first.
    const [sortDir, setSortDir] = useState('desc');
    const [dateRange, setDateRange] = useState('all');
    const [activeTag, setActiveTag] = useState(searchParams.get('tag') || null); // clicked-chip tag filter (deep-linkable: /ai-templates?tag=x)
    const [favOnly, setFavOnly] = useState(false); // ❤️ header toggle — show only favorited templates
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    // Bumped by the sort-direction button to force the fetch effect to re-run
    // and refetch after the cache is cleared (see handleSortDirToggle).
    const [reloadNonce, setReloadNonce] = useState(0);

    // Detected colour bucket per template id (populated async from thumbnails).
    const [colorBuckets, setColorBuckets] = useState({});

    const fmt = (n) => (n ?? 0).toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US');
    const getCatName = (cat) => (i18n.language === 'bn' && cat?.nameBn) ? cat.nameBn : cat?.name;
    const getCatDesc = (cat) => (i18n.language === 'bn' && cat?.descriptionBn) ? cat.descriptionBn : cat?.description;

    useEffect(() => {
        setActiveCategory(searchParams.get('category') || 'all');
        // Tag deep-links (?tag=x from the generate page's tag chips). Chip clicks
        // inside the gallery stay state-only, so they never re-trigger this.
        setActiveTag(searchParams.get('tag') || null);
    }, [searchParams]);

    const handleCategoryChange = (cat) => {
        // Picking a category is a fresh browse — clear the tag & other active filters.
        setActiveTag(null);
        setSearchQuery('');
        setOrientation('all');
        setImageCount('all');
        setColor('all');
        setDateRange('all');
        if (cat === 'all') setSearchParams({});
        else setSearchParams({ category: cat });
    };

    // ── Fetch (12h client-side TTL cache) ─────────────────────────────────
    // If both templates and categories are cached and still fresh, serve them
    // directly and skip the network entirely. Otherwise fetch and cache.
    useEffect(() => {
        let cancelled = false;
        // Trim leading/trailing spaces so " word " and "word" search the same.
        const trimmedSearch = (debouncedSearch || '').trim();
        const key = trimmedSearch;

        const freshTpls = cacheGetTemplates(key);
        const freshCats = cacheGetCategories();

        if (freshTpls !== undefined && freshCats !== undefined) {
            setTemplates(freshTpls);
            setCategories(freshCats.filter(c => c.status === 'published'));
            setLoading(false);
            // Categories are a tiny payload — revalidate them in the background so
            // admin pin changes (the 📌 section) propagate on the next visit instead
            // of waiting out the 12h template cache.
            aiTemplateCategoryService.list().then(cats => {
                if (cancelled) return;
                const rawCats = cats?.data || [];
                cacheSetCategories(rawCats);
                setCategories(rawCats.filter(c => c.status === 'published'));
            }).catch(() => {});
            return; // templates fully cached within TTL — no template DB call
        }

        setLoading(true);
        Promise.all([
            aiTemplateService.list({ search: trimmedSearch }),
            aiTemplateCategoryService.list(),
        ]).then(([list, cats]) => {
            if (cancelled) return;
            const tpls = list?.data || [];
            const rawCats = cats?.data || [];
            cacheSetTemplates(key, tpls);
            cacheSetCategories(rawCats);
            setTemplates(tpls);
            setCategories(rawCats.filter(c => c.status === 'published'));
        }).catch(() => {
            if (!cancelled) setTemplates([]);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [debouncedSearch, reloadNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // Self-heal stale thumbnails. The 12h cache can serve an OLD thumbnail URL after
    // an admin changes it — the old R2 file is deleted, so the URL 404s and the card
    // shows a broken image. On the FIRST such error, wipe the cache and refetch once
    // so the fresh URLs load. Guarded by a ref so a genuinely-missing image can't loop
    // (after the one refetch, a still-broken thumbnail just falls back to a placeholder).
    const healedRef = useRef(false);
    const handleThumbError = useCallback(() => {
        if (healedRef.current) return;
        healedRef.current = true;
        useAiTemplateCacheStore.getState().clear();
        setReloadNonce(n => n + 1);
    }, []);

    // The sort-direction button also force-refreshes the gallery: wipe the
    // localStorage cache and bump the reload nonce so the fetch effect re-runs,
    // refetches, and re-caches the latest server response (replacing stale cache).
    const handleSortDirToggle = () => {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
        useAiTemplateCacheStore.getState().clear();
        setReloadNonce(n => n + 1);
    };

    // Load all AI-template tags once for the marquee.
    useEffect(() => {
        let cancelled = false;
        aiTemplateService.tags()
            .then(r => { if (!cancelled) setAllTags((r?.data || []).map(tg => tg.name).filter(Boolean)); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Tag marquee: keep the SPEED constant (px/sec) regardless of how many tags there
    // are. The row is duplicated for a seamless loop, so it travels one set-width (half
    // the total) per cycle; duration = that width ÷ speed. Without this, a fixed duration
    // makes the scroll fast when there are many tags (live) and slow with few (dev).
    const TAG_MARQUEE_SPEED = 22; // pixels per second — slow, smooth, constant
    const tagRowRef = useRef(null);
    const [tagMarqueeDuration, setTagMarqueeDuration] = useState(60);
    const [tagMarqueePaused, setTagMarqueePaused] = useState(false); // pause on hover
    useEffect(() => {
        const el = tagRowRef.current;
        if (!el || allTags.length === 0) return;
        const measure = () => {
            const oneSetWidth = el.scrollWidth / 2; // duplicated → half is one full set
            if (oneSetWidth > 0) setTagMarqueeDuration(oneSetWidth / TAG_MARQUEE_SPEED);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [allTags]);

    // Detect colour buckets from thumbnails — ONLY once the Colour filter is in use.
    // Reading a thumbnail's dominant colour requires loading the full image, so doing
    // this for all ~1500 templates on mount would pull the entire gallery up front and
    // defeat the cards' lazy-loading. The swatches are static and don't need buckets to
    // render, so we defer the scan until the user actually picks a colour; buckets then
    // fill in and the grid narrows progressively as each resolves.
    useEffect(() => {
        if (color === 'all') return;
        let cancelled = false;
        templates.forEach((tp) => {
            if (!tp.thumbnail || colorBuckets[tp.id] !== undefined) return;
            imageColorBucket(tp.thumbnail).then((bucket) => {
                if (!cancelled) setColorBuckets(prev => (prev[tp.id] !== undefined ? prev : { ...prev, [tp.id]: bucket }));
            });
        });
        return () => { cancelled = true; };
    }, [templates, color]); // eslint-disable-line react-hooks/exhaustive-deps

    const publishedCatIds = useMemo(() => new Set(categories.map(c => c.id)), [categories]);

    const activeCategoryId = useMemo(() => {
        if (activeCategory === 'all') return null;
        return categories.find(c => c.slug === activeCategory)?.id ?? null;
    }, [activeCategory, categories]);

    const activeCategoryObj = activeCategory === 'all' ? null : categories.find(c => c.slug === activeCategory);

    // Templates that belong to a published category (the visible universe).
    const visibleUniverse = useMemo(
        () => templates.filter(tp => !tp.categoryRef?.id || publishedCatIds.has(tp.categoryRef.id)),
        [templates, publishedCatIds],
    );

    const countByCatId = useMemo(() => {
        const map = new Map();
        for (const tp of visibleUniverse) {
            if (tp.categoryId == null) continue;
            map.set(tp.categoryId, (map.get(tp.categoryId) || 0) + 1);
        }
        return map;
    }, [visibleUniverse]);

    const imgCountOf = (tp) => {
        const n = tp.requiredImageCount || 1;
        if (n >= 3) return '3+';   // group: more than 2 people
        if (n === 2) return '2';   // couple / family
        return '1';                 // single portrait
    };

    // ── Apply filters + sort ──────────────────────────────────────────────
    const filtered = useMemo(() => {
        let list = visibleUniverse;
        if (activeCategoryId != null) list = list.filter(tp => tp.categoryId === activeCategoryId);
        if (orientation !== 'all') list = list.filter(tp => (ORIENTATION_OF[tp.imageSize] || 'portrait') === orientation);
        if (imageCount !== 'all') list = list.filter(tp => imgCountOf(tp) === imageCount);
        if (color !== 'all') list = list.filter(tp => colorBuckets[tp.id] === color);
        if (gender !== 'all') list = list.filter(tp => Array.isArray(tp.genders) && tp.genders.includes(gender));
        if (dateRange !== 'all') {
            const cutoff = dateRangeCutoff(dateRange);
            if (cutoff != null) list = list.filter(tp => new Date(tp.createdAt || 0).getTime() >= cutoff);
        }
        if (activeTag) list = list.filter(tp => (tp.tags || []).some(t => t.toLowerCase() === activeTag.toLowerCase()));

        const sorted = [...list];
        // Base comparators are DESC (highest / newest first); `dir` flips them for the
        // Newest/Popular tabs via the asc/desc toggle. Oldest/name keep fixed order.
        const dir = sortDir === 'asc' ? -1 : 1;
        // Trending: most uses in the last 7 days first (trendingCount comes from the list API).
        // Templates with no recent use follow, ordered by lifetime usage — so a category with
        // nothing trending is never empty and the default view always shows the whole gallery.
        if (sort === 'trending') sorted.sort((a, b) => dir * (((b.trendingCount || 0) - (a.trendingCount || 0)) || ((b.usageCount || 0) - (a.usageCount || 0)) || (new Date(b.createdAt || 0) - new Date(a.createdAt || 0))) || (b.id > a.id ? 1 : -1));
        else if (sort === 'popular') sorted.sort((a, b) => dir * ((b.usageCount || 0) - (a.usageCount || 0)) || (b.id > a.id ? 1 : -1));
        else if (sort === 'newest') sorted.sort((a, b) => dir * (new Date(b.createdAt || 0) - new Date(a.createdAt || 0)) || (b.id > a.id ? 1 : -1));
        else if (sort === 'oldest') sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0) || (a.id > b.id ? 1 : -1));
        else if (sort === 'name') sorted.sort((a, b) => String(getCatName(a) || a.name || '').localeCompare(String(getCatName(b) || b.name || ''), i18n.language));
        return sorted;
    }, [visibleUniverse, activeCategoryId, orientation, imageCount, color, colorBuckets, gender, sort, sortDir, dateRange, activeTag, i18n.language]); // eslint-disable-line

    // Reset paging whenever the result set changes shape.
    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, orientation, imageCount, color, gender, sort, sortDir, dateRange, activeTag, debouncedSearch, favOnly]);

    const filtersDirty = activeCategory !== 'all' || orientation !== 'all' || imageCount !== 'all' || color !== 'all' || gender !== 'all' || sort !== 'trending' || sortDir !== 'desc' || dateRange !== 'all' || !!activeTag || !!searchQuery;

    // ── Default-view ordering + favorites filter ──────────────────────────
    // ❤️ toggle on → the grid shows ONLY favorited templates. Otherwise, the
    // untouched gallery floats the admin-pinned category's templates to the
    // front (one flat grid, stable sort); any search/filter/sort (filtersDirty)
    // switches that off and the grid is exactly what the user asked for.
    const sectioned = !filtersDirty && !favOnly;
    const pinnedCategory = useMemo(() => categories.find(c => c.pinned) || null, [categories]);
    const orderedTemplates = useMemo(() => {
        if (favOnly) return filtered.filter(tp => favSet.has(tp.id));
        if (!sectioned) return filtered;
        const pinnedId = pinnedCategory?.id;
        if (pinnedId == null) return filtered;
        const rank = (tp) => (tp.categoryId === pinnedId ? 1 : 0);
        return [...filtered].sort((a, b) => rank(b) - rank(a));
    }, [favOnly, sectioned, filtered, favSet, pinnedCategory]);

    const shown = orderedTemplates.slice(0, visibleCount);
    const hasMore = orderedTemplates.length > visibleCount;

    // Infinite scroll: reveal the next page automatically when the sentinel
    // scrolls into view, replacing the old "load more" button.
    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!hasMore) return;
        const node = sentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setVisibleCount(c => c + PAGE_SIZE);
                }
            },
            { rootMargin: '400px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, shown.length]);

    const resetFilters = () => {
        handleCategoryChange('all');
        setOrientation('all');
        setImageCount('all');
        setColor('all');
        setGender('all');
        setSort('trending');
        setSortDir('desc');
        setDateRange('all');
        setActiveTag(null);
        setSearchQuery('');
        setFavOnly(false);
    };

    // Filter controls are shared between the desktop sidebar and the mobile
    // drawer (Sheet), so they render identically in both places.
    const filterHeaderRow = (
        <div className="flex items-center gap-2 mb-4">
            <IconAdjustmentsHorizontal size={17} className="text-[#E2702E]" />
            <span className="text-sm font-bold text-[#1C1B19] dark:text-zinc-100">{t('worldCupJersey.filters')}</span>
            <button
                onClick={resetFilters}
                disabled={!filtersDirty}
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold transition-colors text-[#E2702E] hover:text-[#E2702E]/80 disabled:text-[#C3BEB4] dark:disabled:text-zinc-600 disabled:cursor-not-allowed"
            >
                <IconRefresh size={13} />
                {t('worldCupJersey.reset')}
            </button>
        </div>
    );

    const filterBody = (
        <>
            {/* Category — scrolls on the Y axis once there are more than 10 rows */}
            <FilterLabel>{t('worldCupJersey.category')}</FilterLabel>
            <div className={`flex flex-col gap-0.5 mb-5 ${categories.length + 1 > 10 ? 'max-h-[400px] overflow-y-auto pr-1 [scrollbar-width:thin]' : ''}`}>
                {loading ? (
                    <CategoryRowsSkeleton />
                ) : (
                    <>
                        <CategoryRow active={activeCategory === 'all'} label={t('worldCupJersey.all')} count={fmt(visibleUniverse.length)} onClick={() => handleCategoryChange('all')} />
                        {categories.map(c => (
                            <CategoryRow key={c.slug} active={activeCategory === c.slug} label={getCatName(c)} count={fmt(countByCatId.get(c.id) || 0)} onClick={() => handleCategoryChange(c.slug)} />
                        ))}
                    </>
                )}
            </div>

            {/* Gender / audience — admin-assigned tags on each template */}
            <FilterLabel>{t('worldCupJersey.genderLabel')}</FilterLabel>
            <div className="grid grid-cols-3 gap-1.5 mb-5">
                <OrientationBtn active={gender === 'all'} icon={IconUsers} label={t('worldCupJersey.all')} onClick={() => setGender('all')} />
                <OrientationBtn active={gender === 'male'} icon={IconGenderMale} label={t('worldCupJersey.genderMale')} onClick={() => setGender('male')} />
                <OrientationBtn active={gender === 'female'} icon={IconGenderFemale} label={t('worldCupJersey.genderFemale')} onClick={() => setGender('female')} />
                <OrientationBtn active={gender === 'children'} icon={IconMoodKid} label={t('worldCupJersey.genderChildren')} onClick={() => setGender('children')} />
                <OrientationBtn active={gender === 'product'} icon={IconShoppingBag} label={t('worldCupJersey.genderProduct')} onClick={() => setGender('product')} />
            </div>

            {/* Image count */}
            <FilterLabel>{t('worldCupJersey.imageCountLabel')}</FilterLabel>
            <div className="flex flex-col gap-0.5 mb-5">
                <RadioRow active={imageCount === 'all'} title={t('worldCupJersey.all')} onClick={() => setImageCount('all')} />
                <RadioRow active={imageCount === '1'} title={t('worldCupJersey.singleImage')} hint={t('worldCupJersey.singleImageHint')} onClick={() => setImageCount('1')} />
                <RadioRow active={imageCount === '2'} title={t('worldCupJersey.dualImage')} hint={t('worldCupJersey.dualImageHint')} onClick={() => setImageCount('2')} />
                <RadioRow active={imageCount === '3+'} title={t('worldCupJersey.multiImage')} hint={t('worldCupJersey.multiImageHint')} onClick={() => setImageCount('3+')} />
            </div>

            {/* Orientation */}
            <FilterLabel>{t('worldCupJersey.orientation')}</FilterLabel>
            <div className="grid grid-cols-4 gap-1.5 mb-5">
                <OrientationBtn active={orientation === 'all'} icon={IconLayoutGrid} label={t('worldCupJersey.all')} onClick={() => setOrientation('all')} />
                <OrientationBtn active={orientation === 'portrait'} icon={IconRectangleVertical} label={t('worldCupJersey.portrait')} onClick={() => setOrientation('portrait')} />
                <OrientationBtn active={orientation === 'landscape'} icon={IconRectangle} label={t('worldCupJersey.landscape')} onClick={() => setOrientation('landscape')} />
                <OrientationBtn active={orientation === 'square'} icon={IconSquare} label={t('worldCupJersey.square')} onClick={() => setOrientation('square')} />
            </div>

            {/* Colour */}
            <FilterLabel>{t('worldCupJersey.color')}</FilterLabel>
            <div className="flex flex-wrap gap-2.5 mb-5">
                <Swatch active={color === 'all'} title={t('worldCupJersey.colorAll')} swatchCls="bg-[conic-gradient(from_0deg,#ef4444,#f59e0b,#eab308,#22c55e,#3b82f6,#a855f7,#ef4444)]" onClick={() => setColor('all')} />
                <Swatch active={color === 'red'} title={t('worldCupJersey.colorRed')} swatchCls="bg-red-500" onClick={() => setColor('red')} />
                <Swatch active={color === 'orange'} title={t('worldCupJersey.colorOrange')} swatchCls="bg-orange-500" onClick={() => setColor('orange')} />
                <Swatch active={color === 'yellow'} title={t('worldCupJersey.colorYellow')} swatchCls="bg-yellow-400" onClick={() => setColor('yellow')} />
                <Swatch active={color === 'green'} title={t('worldCupJersey.colorGreen')} swatchCls="bg-green-500" onClick={() => setColor('green')} />
                <Swatch active={color === 'blue'} title={t('worldCupJersey.colorBlue')} swatchCls="bg-blue-500" onClick={() => setColor('blue')} />
                <Swatch active={color === 'purple'} title={t('worldCupJersey.colorPurple')} swatchCls="bg-purple-500" onClick={() => setColor('purple')} />
                <Swatch active={color === 'neutral'} title={t('worldCupJersey.colorNeutral')} swatchCls="bg-[linear-gradient(135deg,#a1a1aa,#3f3f46)]" onClick={() => setColor('neutral')} />
            </div>

            {/* Sort */}
            <FilterLabel>{t('worldCupJersey.sort')}</FilterLabel>
            <div className="flex flex-col gap-0.5">
                <SortRow active={sort === 'trending'} icon={IconFlame} label={t('worldCupJersey.sortTrending')} onClick={() => setSort('trending')} />
                <SortRow active={sort === 'popular'} icon={IconTrendingUp} label={t('worldCupJersey.sortPopular')} onClick={() => setSort('popular')} />
                <SortRow active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                <SortRow active={sort === 'oldest'} icon={IconClock} label={t('worldCupJersey.sortOldest')} onClick={() => setSort('oldest')} />
                <SortRow active={sort === 'name'} icon={IconSortAscendingLetters} label={t('worldCupJersey.sortName')} onClick={() => setSort('name')} />
            </div>
        </>
    );

    return (
        <div className="font-hind w-full py-4 md:py-6">
            {user?.role === 'OWNER' && (
                <LowCreditBanner credit={effectiveCreditForBanner} className="mb-4" />
            )}
            {/* Header — title/subtitle on the left, quick New/Popular sort pills on the right */}
            <div className="mb-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                        {activeCategoryObj ? getCatName(activeCategoryObj) : t('worldCupJersey.heroTitle')}
                    </h1>
                    <p className="mt-1 text-sm text-[#6E6A63] dark:text-zinc-300">
                        {activeCategoryObj ? getCatDesc(activeCategoryObj) : t('worldCupJersey.heroSubtitle')}
                    </p>
                </div>
                {/* Quick one-click sort: New / Popular (mirrors the dropdown's two
                    most-used options so users can toggle them directly). */}
                <div className="flex-shrink-0 flex items-center gap-2">
                    <div className="flex items-center gap-0.5 p-0.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                        <QuickSortPill active={sort === 'trending'} icon={IconFlame} label={t('worldCupJersey.sortTrending')} onClick={() => setSort('trending')} />
                        <QuickSortPill active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                        <QuickSortPill active={sort === 'popular'} icon={IconTrendingUp} label={t('worldCupJersey.sortPopular')} onClick={() => setSort('popular')} />
                    </div>
                    {/* Direction toggle — flips the active tab between desc (default) and asc. */}
                    <button
                        onClick={handleSortDirToggle}
                        title={sortDir === 'desc' ? t('worldCupJersey.sortDesc') : t('worldCupJersey.sortAsc')}
                        aria-label={sortDir === 'desc' ? t('worldCupJersey.sortDesc') : t('worldCupJersey.sortAsc')}
                        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer"
                    >
                        {sortDir === 'desc'
                            ? <IconSortAscending size={16} className="text-[#E2702E]" />
                            : <IconSortDescending size={16} className="text-[#E2702E]" />}
                        <span className="hidden sm:inline">{sortDir === 'desc' ? t('worldCupJersey.sortToggleAsc') : t('worldCupJersey.sortToggleDesc')}</span>
                    </button>
                    {/* ❤️ Favorites-only toggle — shows just the templates the user hearted. */}
                    <button
                        onClick={() => setFavOnly(v => !v)}
                        title={t('worldCupJersey.favoritesSection')}
                        aria-pressed={favOnly}
                        className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-[11px] border text-[13px] font-semibold transition-colors cursor-pointer ${favOnly
                            ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                            : 'border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'}`}
                    >
                        {favOnly ? <IconHeartFilled size={16} className="text-red-500" /> : <IconHeart size={16} className="text-[#E2702E]" />}
                        <span className="hidden sm:inline">{t('worldCupJersey.favoritesSection')}</span>
                    </button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-5 items-start">

                {/* ───────────────── Sidebar (desktop, lg and up) ───────────────── */}
                <aside className="hidden lg:block lg:w-72 lg:flex-shrink-0 lg:sticky lg:top-4 self-start bg-white dark:bg-zinc-900 border border-[#ECE9E3] dark:border-zinc-700 rounded-2xl p-4">
                    {filterHeaderRow}
                    {filterBody}
                </aside>

                {/* ─────────────────────────── Main ─────────────────────────── */}
                <main ref={gridRef} className="flex-1 min-w-0 w-full">
                    {/* Search + mobile filter trigger */}
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-2.5 h-12 px-3.5 flex-1 min-w-0 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                            <IconSearch size={18} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
                            <input
                                type="text"
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
                                placeholder={t('worldCupJersey.searchPlaceholder')}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    aria-label={t('worldCupJersey.clearSearch')}
                                    className="flex-shrink-0 p-0.5 rounded-full text-[#9C988F] dark:text-zinc-400 hover:text-[#E2702E] hover:bg-[#F1EFEA] dark:hover:bg-zinc-800 transition-colors"
                                >
                                    <IconX size={16} />
                                </button>
                            )}
                        </div>
                        {/* Only shown below lg — opens the filter drawer */}
                        <button
                            onClick={() => setFilterSheetOpen(true)}
                            className="lg:hidden relative flex-shrink-0 inline-flex items-center gap-1.5 h-12 px-4 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors"
                        >
                            <IconAdjustmentsHorizontal size={18} className="text-[#E2702E]" />
                            <span className="hidden sm:inline">{t('worldCupJersey.filters')}</span>
                            {filtersDirty && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#E2702E]" />}
                        </button>
                    </div>

                    {/* Tag marquee (replaces the popular-category chips) + result count + sort */}
                    <div className="flex items-center gap-3 mb-4">
                        {allTags.length > 0 ? (
                            <div
                                className="flex-1 min-w-0 relative overflow-hidden"
                                onMouseEnter={() => setTagMarqueePaused(true)}
                                onMouseLeave={() => setTagMarqueePaused(false)}
                                style={{ WebkitMaskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)', maskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)' }}
                            >
                                <div
                                    ref={tagRowRef}
                                    className="flex gap-2 w-max py-0.5"
                                    style={{
                                        animationName: 'atg-tag-marquee',
                                        animationDuration: `${tagMarqueeDuration}s`,
                                        animationTimingFunction: 'linear',
                                        animationIterationCount: 'infinite',
                                        animationPlayState: tagMarqueePaused ? 'paused' : 'running',
                                    }}
                                >
                                    {[...allTags, ...allTags].map((tag, i) => {
                                        const active = activeTag && activeTag.toLowerCase() === tag.toLowerCase();
                                        return (
                                            <button
                                                key={`${tag}-${i}`}
                                                onClick={() => setActiveTag(active ? null : tag)}
                                                className={`flex-shrink-0 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${active
                                                    ? 'bg-[#E2702E] border-[#E2702E] text-white'
                                                    : 'text-[#6E6A63] dark:text-zinc-300 bg-white dark:bg-zinc-800 border-[#E9E6E0] dark:border-zinc-700 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 hover:text-[#E2702E]'}`}
                                            >
                                                #{tag}
                                            </button>
                                        );
                                    })}
                                </div>
                                <style>{`@keyframes atg-tag-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
                            </div>
                        ) : <div className="flex-1" />}
                        <div className="flex-shrink-0 flex items-center gap-2.5">
                            <p className="text-[13px] text-[#6E6A63] dark:text-zinc-300 whitespace-nowrap hidden sm:block">
                                {fmt(orderedTemplates.length)} {t('worldCupJersey.templatesFound')}
                            </p>
                            <SortMenu sort={sort} setSort={setSort} dateRange={dateRange} setDateRange={setDateRange} />
                        </div>
                    </div>

                    {/* Skeleton */}
                    {loading && <SkeletonMasonry />}

                    {/* Empty */}
                    {!loading && orderedTemplates.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-20">
                            <div className="w-16 h-16 rounded-2xl bg-[#F1EFEA] dark:bg-zinc-800 flex items-center justify-center">
                                {favOnly
                                    ? <IconHeart size={28} className="text-[#C3BEB4] dark:text-zinc-500" />
                                    : <IconSparkles size={28} className="text-[#C3BEB4] dark:text-zinc-500" />}
                            </div>
                            <p className="text-sm text-[#6E6A63] dark:text-zinc-300">
                                {favOnly ? t('worldCupJersey.favoritesEmpty') : t('worldCupJersey.noTemplates')}
                            </p>
                        </div>
                    )}

                    {/* Active tag filter banner */}
                    {activeTag && (
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-sm text-zinc-500 dark:text-zinc-400">{t('worldCupJersey.filters')}:</span>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#E2702E]/10 text-[#E2702E]">
                                #{activeTag}
                                <button onClick={() => setActiveTag(null)} className="hover:text-red-500 transition-colors">
                                    <IconX size={12} />
                                </button>
                            </span>
                        </div>
                    )}

                    {/* Grid — with ❤️ favorites + 📌 pinned-category sections in the default view */}
                    {!loading && orderedTemplates.length > 0 && (() => {
                        const renderCard = (tpl) => (
                            <TemplateCard
                                key={tpl.id}
                                template={tpl}
                                onMeasure={reportRatio}
                                trendingCount={sort === 'trending' ? (tpl.trendingCount || 0) : 0}
                                onClick={() => navigate(`/ai-templates/${tpl.slug}`)}
                                onTagClick={setActiveTag}
                                isFavorite={favSet.has(tpl.id)}
                                onToggleFavorite={() => toggleFavorite(tpl.id)}
                                onThumbError={handleThumbError}
                            />
                        );
                        return (
                            <>
                                <BalancedMasonry items={shown} columnCount={columnCount} heightOf={heightOf} renderItem={renderCard} />

                                {hasMore && (
                                    <div ref={sentinelRef} className="flex justify-center items-center gap-2 mt-6 py-4 text-[#E2702E]">
                                        <IconLoader2 size={20} className="animate-spin" />
                                        <span className="text-sm font-medium">{t('common.loading')}</span>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </main>
            </div>

            {/* ───────────── Filter drawer (below lg) ───────────── */}
            <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetContent side="left" className="w-[86%] max-w-xs overflow-y-auto p-4 font-hind">
                    <SheetTitle className="sr-only">{t('worldCupJersey.filters')}</SheetTitle>
                    <div className="pr-7">{filterHeaderRow}</div>
                    {filterBody}
                </SheetContent>
            </Sheet>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sidebar primitives
// ---------------------------------------------------------------------------
function FilterLabel({ children }) {
    return <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400 mb-2">{children}</p>;
}

function CategoryRowsSkeleton({ rows = 6 }) {
    return (
        <>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg animate-pulse">
                    <span className="h-3 rounded bg-zinc-200 dark:bg-zinc-700" style={{ width: `${45 + ((i * 13) % 40)}%` }} />
                    <span className="ml-2 flex-shrink-0 h-4 w-7 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                </div>
            ))}
        </>
    );
}

function CategoryRow({ active, label, count, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active
                    ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30'
                    : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className="truncate text-left">{label}</span>
            <span className={`ml-2 flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                active ? 'text-[#E2702E] bg-white dark:bg-zinc-900' : 'text-[#A8A294] dark:text-zinc-400 bg-[#F1EFEA] dark:bg-zinc-800'
            }`}>{count}</span>
        </button>
    );
}

function OrientationBtn({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            title={label}
            className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-semibold border transition-colors ${
                active
                    ? 'border-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30 text-[#E2702E]'
                    : 'border-[#ECE9E3] dark:border-zinc-700 text-[#6E6A63] dark:text-zinc-300 hover:border-[#E2702E]/50'
            }`}
        >
            <Icon size={15} />
            <span className="truncate w-full text-center px-0.5">{label}</span>
        </button>
    );
}

function RadioRow({ active, title, hint, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                active ? 'bg-[#FCEFE7] dark:bg-orange-950/30' : 'hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className={`w-4 h-4 rounded-full flex-shrink-0 border-[1.5px] transition-colors ${
                active ? 'border-[4.5px] border-[#E2702E]' : 'border-[#C3BEB4] dark:border-zinc-600'
            }`} />
            <span className="flex flex-col leading-tight">
                <span className="text-[13px] font-medium text-[#1C1B19] dark:text-zinc-100">{title}</span>
                {hint && <span className="text-[10.5px] text-[#9C988F] dark:text-zinc-400">{hint}</span>}
            </span>
        </button>
    );
}

function Swatch({ active, title, swatchCls, onClick }) {
    return (
        <button
            onClick={onClick}
            title={title}
            className={`w-[34px] h-[34px] rounded-full transition-all ${swatchCls} ${
                active ? 'outline outline-2 outline-[#E2702E] outline-offset-2' : 'outline outline-1 outline-[#ECE9E3] outline-offset-2'
            }`}
        />
    );
}

// Compact segmented pill for the top-right one-click New / Popular toggles.
function QuickSortPill({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-[9px] text-[13px] font-semibold transition-colors cursor-pointer ${
                active
                    ? 'bg-[#E2702E] text-white'
                    : 'text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
            }`}
        >
            <Icon size={15} className={active ? 'text-white' : 'text-[#E2702E]'} />
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
}

function SortRow({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30' : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <Icon size={14} />
            {label}
        </button>
    );
}

// ---------------------------------------------------------------------------
// Top-right Sort / date-range dropdown (mirrors the sidebar sort + adds a
// "created within" date filter: today, last 3 / 7 / 30 days).
// ---------------------------------------------------------------------------
function SortMenu({ sort, setSort, dateRange, setDateRange }) {
    const { t } = useTranslation();
    const sortOptions = [
        { value: 'trending', label: t('worldCupJersey.sortTrending') },
        { value: 'popular', label: t('worldCupJersey.sortPopular') },
        { value: 'newest', label: t('worldCupJersey.sortNewest') },
        { value: 'oldest', label: t('worldCupJersey.sortOldest') },
        { value: 'name', label: t('worldCupJersey.sortName') },
    ];
    const dateOptions = [
        { value: 'all', label: t('worldCupJersey.dateAll') },
        { value: 'today', label: t('worldCupJersey.dateToday') },
        { value: '3days', label: t('worldCupJersey.date3Days') },
        { value: '7days', label: t('worldCupJersey.date7Days') },
        { value: '30days', label: t('worldCupJersey.date30Days') },
    ];
    const active = sort !== 'trending' || dateRange !== 'all';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="relative inline-flex items-center gap-1.5 h-9 px-3 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer">
                    <IconArrowsSort size={16} className="text-[#E2702E]" />
                    <span className="hidden sm:inline">{t('worldCupJersey.sortAction')}</span>
                    <IconChevronDown size={14} className="text-[#9C988F] dark:text-zinc-400" />
                    {active && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#E2702E]" />}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 font-hind">
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconArrowsSort size={13} /> {t('worldCupJersey.sortBy')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={sort} onValueChange={setSort}>
                    {sortOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">
                            {o.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconCalendar size={13} /> {t('worldCupJersey.dateRange')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={dateRange} onValueChange={setDateRange}>
                    {dateOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">
                            {o.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ---------------------------------------------------------------------------
// Template card — natural-aspect thumbnail so masonry packs without gaps.
// ---------------------------------------------------------------------------
function TemplateCard({ template, onClick, onMeasure, onTagClick, isFavorite = false, onToggleFavorite, onThumbError, trendingCount = 0 }) {
    const { t, i18n } = useTranslation();
    const displayName = (i18n.language === 'bn' && template.nameBn) ? template.nameBn : template.name;
    const catName = (i18n.language === 'bn' && template.categoryRef?.nameBn) ? template.categoryRef.nameBn : template.categoryRef?.name;
    // Natural thumbnail ratio (height/width), measured on load — used to size the
    // image box for "auto" templates so landscape thumbnails render landscape.
    const [measuredRatio, setMeasuredRatio] = useState(null);
    const [loaded, setLoaded] = useState(false); // show a skeleton until the thumbnail loads
    const [errored, setErrored] = useState(false); // thumbnail 404'd — show placeholder, not a broken box
    const imgRef = useRef(null);
    const prevThumbRef = useRef(template.thumbnail);
    const handleLoaded = (el) => {
        const { naturalWidth, naturalHeight } = el;
        if (naturalWidth) {
            const r = naturalHeight / naturalWidth;
            setMeasuredRatio(r);
            onMeasure?.(template.id, r);
        }
        setLoaded(true);
    };
    // Reset ONLY when the thumbnail actually changes (post cache self-heal → clean retry).
    // A plain mount-time reset races the image: a cached / fast image fires `load` (→ loaded=true)
    // BEFORE React flushes this passive effect, which then flipped it back to false and left the
    // skeleton on a fully-loaded image forever.
    useEffect(() => {
        if (prevThumbRef.current !== template.thumbnail) {
            prevThumbRef.current = template.thumbnail;
            setErrored(false);
            setLoaded(false);
        }
    }, [template.thumbnail]);
    // Safety net: if the image already finished loading before React attached its handler
    // (cache hit, remounted card), no `load` event will ever fire — pick it up here.
    useEffect(() => {
        const el = imgRef.current;
        if (!loaded && el && el.complete && el.naturalWidth > 0) handleLoaded(el);
    }, [loaded, errored, template.thumbnail]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div
            onClick={onClick}
            className="group mb-4 rounded-2xl overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 cursor-pointer relative transition-all duration-150 hover:shadow-lg hover:-translate-y-0.5 hover:border-[#E2702E]/40"
        >
            <div className="relative">
                {template.thumbnail && !errored ? (
                    // Reserve the thumbnail's height up-front (known bucket, measured
                    // ratio, or a portrait default) so the card never collapses, and
                    // show a skeleton shimmer that fades into the image on load.
                    <div
                        className="relative w-full overflow-hidden bg-[#F0EFEC] dark:bg-zinc-800"
                        style={{
                            aspectRatio:
                                IMG_ASPECT_RATIO[template.imageSize] ||
                                (measuredRatio ? `1 / ${measuredRatio}` : '4 / 5'),
                        }}
                    >
                        {!loaded && <div className="absolute inset-0 animate-pulse bg-[#E9E7E2] dark:bg-zinc-700/60" />}
                        <img
                            ref={imgRef}
                            src={template.thumbnail}
                            alt={displayName}
                            loading="lazy"
                            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                            onLoad={(e) => handleLoaded(e.currentTarget)}
                            onError={() => { setLoaded(true); setErrored(true); onThumbError?.(); }}
                        />
                    </div>
                ) : (
                    <div className="w-full aspect-[3/4] flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800">
                        <IconSparkles size={28} className="text-[#C3BEB4] dark:text-zinc-500" />
                    </div>
                )}

                {/* Usage badge + favorite heart. The heart's width is collapsed until
                    hover (opacity alone would still reserve space), so the uses badge
                    sits flush right and slides left smoothly to make room. A favorited
                    heart stays expanded permanently. */}
                <div className="absolute top-2.5 right-2.5 z-10 flex items-center">
                    {trendingCount > 0 ? (
                        // Trending view: show this week's uses (the ranking signal) with a flame.
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-white bg-[#E2702E]/90 backdrop-blur-sm px-2.5 py-[3px] rounded-full whitespace-nowrap">
                            <IconFlame size={12} />
                            {t('worldCupJersey.trendingThisWeek', { n: formatUses(trendingCount) })}
                        </span>
                    ) : template.usageCount > 0 && (
                        <span className="text-[10.5px] font-semibold text-white bg-black/55 backdrop-blur-sm px-2.5 py-[3px] rounded-full whitespace-nowrap">
                            {formatUses(template.usageCount)} {t('worldCupJersey.uses')}
                        </span>
                    )}
                    {onToggleFavorite && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                            title={isFavorite ? t('worldCupJersey.removeFavorite') : t('worldCupJersey.addFavorite')}
                            className={`h-7 rounded-full backdrop-blur-sm flex items-center justify-center flex-shrink-0 overflow-hidden transition-all duration-200 ease-out ${
                                isFavorite
                                    ? 'w-7 ml-1.5 opacity-100 bg-white/90 text-red-500'
                                    : 'w-0 ml-0 opacity-0 group-hover:w-7 group-hover:ml-1.5 group-hover:opacity-100 focus-visible:w-7 focus-visible:ml-1.5 focus-visible:opacity-100 bg-black/40 text-white hover:bg-black/60'
                            }`}
                        >
                            <span className="flex-shrink-0 flex items-center justify-center">
                                {isFavorite ? <IconHeartFilled size={15} /> : <IconHeart size={15} />}
                            </span>
                        </button>
                    )}
                </div>

                {/* Free badge — this template costs no credit to generate */}
                {template.isFree && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="absolute top-2.5 left-2.5 z-10 text-[10.5px] font-bold text-white bg-emerald-500 px-2.5 py-[3px] rounded-full shadow-sm cursor-help">
                                {t('profile.creditFree')}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{t('profile.freeTemplateTooltip')}</TooltipContent>
                    </Tooltip>
                )}

                {/* Hover overlay: centered CTA + name/category pinned to bottom */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/70 via-black/10 to-black/25">
                    {/* Centered CTA */}
                    <div className="absolute inset-0 flex items-center justify-center p-3.5">
                        <div className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#E2702E] text-white text-[12.5px] font-semibold">
                            <IconArrowUp size={14} />
                            {t('worldCupJersey.useThisStyle')}
                        </div>
                    </div>

                    {/* Name & category — absolute bottom, transparent bg */}
                    <div className="absolute inset-x-0 bottom-0 px-3.5 py-2.5">
                        <p className="text-[13px] font-semibold text-white truncate">{displayName}</p>
                        {catName && <p className="text-[11px] text-white/70 mt-0.5 truncate">{catName}</p>}
                        {Array.isArray(template.tags) && template.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {template.tags.slice(0, 3).map(tag => (
                                    <button
                                        key={tag}
                                        onClick={(e) => { e.stopPropagation(); onTagClick?.(tag); }}
                                        className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-white/20 text-white hover:bg-[#E2702E] transition-colors backdrop-blur-sm"
                                    >
                                        #{tag}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function formatUses(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

// ---------------------------------------------------------------------------
// Skeleton masonry — staggered heights to mimic the real grid
// ---------------------------------------------------------------------------
function SkeletonMasonry() {
    const heights = ['h-[260px]', 'h-[200px]', 'h-[300px]', 'h-[230px]', 'h-[280px]', 'h-[210px]', 'h-[320px]', 'h-[240px]', 'h-[260px]'];
    return (
        <Masonry breakpointCols={masonryBreakpoints} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
            {heights.map((h, i) => (
                <div key={i} className={`mb-4 rounded-2xl overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse ${h}`} />
            ))}
        </Masonry>
    );
}
